import { useEffect, useRef, useCallback } from 'react';
import useRealTimeCountdown from '../../hooks/useRealTimeCountdown';

export default function useIdleWarningModalResponsive({ open, logoutAtMs, onLogout }) {
  const seconds = useRealTimeCountdown({
    endAtMs: logoutAtMs,
    onEnd: onLogout,
    visibleUpdateMs: 250,
    hiddenUpdateMs: 1000
  });

  const dialogRef = useRef(null);
  const firstBtnRef = useRef(null);
  const lastBtnRef = useRef(null);

  // Scroll lock when open (prevents background scroll on mobile)
  useEffect(() => {
    if (!open) return;

    const { body } = document;
    const prevOverflow = body.style.overflow;
    const prevTouch = body.style.touchAction;
    const prevScroll = body.style.overscrollBehavior;

    body.style.overflow = 'hidden';
    body.style.touchAction = 'none';
    body.style.overscrollBehavior = 'contain';

    return () => {
      body.style.overflow = prevOverflow;
      body.style.touchAction = prevTouch;
      body.style.overscrollBehavior = prevScroll;
    };
  }, [open]);

  // Focus trap + initial focus
  useEffect(() => {
    if (open) {
      const to = setTimeout(() => {
        firstBtnRef.current?.focus();
      }, 0);
      return () => clearTimeout(to);
    }
  }, [open]);

  // Focus trap keyboard handler
  const onKeyDown = useCallback((e) => {
    if (e.key !== 'Tab') return;

    const first = firstBtnRef.current;
    const last = lastBtnRef.current;
    if (!first || !last) return;

    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  }, []);

  // Handle escape key (intentionally disabled for security)
  useEffect(() => {
    const handleEscape = (event) => {
      if (event.key === 'Escape' && open) {
        // Intentionally not calling onLogout for security
        // User must make an explicit choice
        event.preventDefault();
      }
    };

    if (open) {
      document.addEventListener('keydown', handleEscape);
      return () => document.removeEventListener('keydown', handleEscape);
    }
  }, [open]);

  return {
    seconds,
    dialogRef,
    firstBtnRef,
    lastBtnRef,
    onKeyDown
  };
}
